import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import * as actionTypes from '@/constants/ActionTypes';
import errorMonitoring from '@/errorMonitoring';
import { selectBtCapabilities, type PaymentMethodIdentity } from '@/selectors/basisTheorySelectors';
import {
  normalizeInstallmentPlans,
  validateBasisTheoryInstallments,
  type InstallmentPlan,
} from 'services/installments';

type InstallmentsCardDetails =
  | {
      kind: 'new';
      bin: string;
      brand: string;
      expirationComplete: boolean;
      last4: string;
    }
  | {
      kind: 'stored';
      brand: string;
      cardType?: 'credit' | 'debit';
      expirationDate?: string;
      id: string;
      installmentsAvailable: boolean;
      last4?: string;
    };

interface InstallmentsCardPreview {
  brand: string;
  cardType?: 'credit' | 'debit';
  expirationDate?: string;
  last4?: string;
}

type InstallmentsEligibilityStatus = 'checking' | 'eligible' | 'error' | 'idle' | 'ineligible';

interface UseInstallmentsEligibilityOptions {
  card?: InstallmentsCardDetails;
  enabled: boolean;
  processor: string;
}

interface UseInstallmentsEligibilityResult {
  retry: () => void;
  status: InstallmentsEligibilityStatus;
}

interface UseBasisTheoryInstallmentsOptions {
  active: boolean;
  card?: InstallmentsCardDetails;
}

interface UseBasisTheoryInstallmentsResult {
  cardPreview?: InstallmentsCardPreview;
  eligibilityStatus: InstallmentsEligibilityStatus;
  plans: InstallmentPlan[];
  retryEligibility: () => void;
  selectMonths: (months: number) => void;
  selectSinglePayment: () => void;
  selectedMonths: number;
  selectedPlan?: InstallmentPlan;
  showInterest: boolean;
  shouldRender: boolean;
  totalAmount?: number;
}

interface InstallmentsReduxState {
  purchase: { get: (key: string) => unknown };
  whitelabelConfig: {
    env: { shared?: { basisTheory?: { engines?: unknown } } };
    features: {
      BIN_VALIDATION_FOR_INSTALLMENTS_ENABLED?: unknown;
      INSTALLMENTS_ENABLED?: unknown;
      SHOW_INSTALLMENTS_INTEREST?: unknown;
    };
  };
}

interface ValidationState {
  key: string;
  status: Exclude<InstallmentsEligibilityStatus, 'idle'>;
}

const BIN_DEBOUNCE_MS = 350;
const SINGLE_PAYMENT_PLAN = { card: '', months: 1, total: 0, monthlyPayment: 0 };

/** Own debounced and stale-safe installment eligibility for the active Basis Theory card. */
function useInstallmentsEligibility({
  card,
  enabled,
  processor,
}: UseInstallmentsEligibilityOptions): UseInstallmentsEligibilityResult {
  const [retryKey, setRetryKey] = useState(0);
  const [validation, setValidation] = useState<ValidationState>({
    key: '',
    status: 'ineligible',
  });
  const newCardBin = card?.kind === 'new' ? card.bin : '';
  const validationKey = useMemo(() => {
    if (
      !enabled ||
      card?.kind !== 'new' ||
      !/^\d{6}$/.test(card.bin) ||
      !card.expirationComplete ||
      !processor.trim()
    ) {
      return '';
    }

    return `${processor.trim()}:${card.bin}:${retryKey}`;
  }, [card, enabled, processor, retryKey]);

  useEffect(() => {
    if (!validationKey || !newCardBin) return undefined;

    let cancelled = false;
    setValidation({ key: validationKey, status: 'checking' });
    const timeoutId = window.setTimeout(async () => {
      try {
        const eligible = await validateBasisTheoryInstallments({
          bin: newCardBin,
          processor,
        });
        if (!cancelled) {
          setValidation({ key: validationKey, status: eligible ? 'eligible' : 'ineligible' });
        }
      } catch (error) {
        if (!cancelled) {
          errorMonitoring.notify({
            error,
            info: { stage: 'basis_theory_installments_validation' },
          });
          setValidation({ key: validationKey, status: 'error' });
        }
      }
    }, BIN_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [newCardBin, processor, validationKey]);

  const retry = useCallback(() => setRetryKey((currentKey) => currentKey + 1), []);

  if (!enabled || !card) return { retry, status: 'idle' };
  if (card.kind === 'stored') {
    return { retry, status: card.installmentsAvailable ? 'eligible' : 'ineligible' };
  }
  if (!validationKey) return { retry, status: 'idle' };

  return { retry, status: validation.key === validationKey ? validation.status : 'checking' };
}

/** Coordinate the active card's eligibility with Purchase's existing selected-plan contract. */
function useBasisTheoryInstallments({
  active,
  card,
}: UseBasisTheoryInstallmentsOptions): UseBasisTheoryInstallmentsResult {
  const dispatch = useDispatch();
  const { binValidationEnabled, installmentsFeatureEnabled, showInterest } = useSelector(
    (state: InstallmentsReduxState) => {
      const paymentMethod = state.purchase.get('selectedPaymentMethod') as
        | PaymentMethodIdentity
        | undefined;
      const capabilities = selectBtCapabilities(state, paymentMethod);

      return {
        binValidationEnabled: capabilities.binValidationEnabled,
        installmentsFeatureEnabled: capabilities.installmentsEnabled,
        showInterest: capabilities.showInstallmentsInterest,
      };
    },
    shallowEqual,
  );
  const processor = useSelector((state: InstallmentsReduxState) => {
    const paymentMethod = state.purchase.get('selectedPaymentMethod') as
      | PaymentMethodIdentity
      | undefined;

    return typeof paymentMethod?.engine === 'string'
      ? paymentMethod.engine.trim().toLowerCase()
      : '';
  });
  const installmentsEnabled = useSelector((state: InstallmentsReduxState) =>
    Boolean(state.purchase.get('enableMonthlySelectedPlan')),
  );
  const storedPaymentPlans = useSelector((state: InstallmentsReduxState) =>
    state.purchase.get('paymentPlans'),
  );
  const paymentPlans = useMemo(() => {
    if (
      typeof storedPaymentPlans === 'object' &&
      storedPaymentPlans !== null &&
      'toJS' in storedPaymentPlans &&
      typeof storedPaymentPlans.toJS === 'function'
    ) {
      return storedPaymentPlans.toJS();
    }

    return storedPaymentPlans;
  }, [storedPaymentPlans]);
  const selectedMonths = useSelector(
    (state: InstallmentsReduxState) => Number(state.purchase.get('monthlySelectedPlan')) || 1,
  );
  const totalAmount = useSelector((state: InstallmentsReduxState) => {
    const total = Number(state.purchase.get('total'));
    return Number.isFinite(total) ? total : undefined;
  });
  const eligibilityEnabled =
    active && installmentsFeatureEnabled && (card?.kind === 'stored' || binValidationEnabled);
  const eligibility = useInstallmentsEligibility({
    card,
    enabled: eligibilityEnabled,
    processor,
  });
  const cardBrand = card?.brand ?? '';
  const plans = useMemo(
    () =>
      eligibility.status === 'eligible' ? normalizeInstallmentPlans(paymentPlans, cardBrand) : [],
    [cardBrand, eligibility.status, paymentPlans],
  );
  const selectedPlan = plans.find((plan) => plan.months === selectedMonths);
  const canSelectInstallments = eligibility.status === 'eligible' && plans.length > 0;
  const cardSelectionKey = card?.kind === 'stored' ? `stored:${card.id}` : `new:${card?.bin ?? ''}`;
  const previousCardSelectionKeyRef = useRef(cardSelectionKey);
  const previousActiveRef = useRef(active);
  const cardChanged = previousCardSelectionKeyRef.current !== cardSelectionKey;

  useEffect(() => {
    const lostOwnership = previousActiveRef.current && !active;
    previousActiveRef.current = active;
    if (!lostOwnership) return;

    if (installmentsEnabled) {
      dispatch({
        type: actionTypes.SET_ENABLE_MONTHLY_SELECTED_PLAN,
        enableMonthlySelectedPlan: false,
      });
    }
    if (selectedMonths <= 1) return;

    dispatch({ type: actionTypes.SELECT_MONTHLY_PLAN, installmentsPlan: SINGLE_PAYMENT_PLAN });
    dispatch({ type: actionTypes.SET_SELECTED_PLAN, selectedPlan: 'singlePayment' });
  }, [active, dispatch, installmentsEnabled, selectedMonths]);

  useEffect(() => {
    previousCardSelectionKeyRef.current = cardSelectionKey;
    if (!active) return;

    if (installmentsEnabled !== canSelectInstallments) {
      dispatch({
        type: actionTypes.SET_ENABLE_MONTHLY_SELECTED_PLAN,
        enableMonthlySelectedPlan: canSelectInstallments,
      });
    }
    if (selectedMonths <= 1) return;
    if (!cardChanged && canSelectInstallments && selectedPlan) return;

    dispatch({ type: actionTypes.SELECT_MONTHLY_PLAN, installmentsPlan: SINGLE_PAYMENT_PLAN });
    dispatch({ type: actionTypes.SET_SELECTED_PLAN, selectedPlan: 'singlePayment' });
  }, [
    active,
    canSelectInstallments,
    cardChanged,
    cardSelectionKey,
    dispatch,
    installmentsEnabled,
    selectedMonths,
    selectedPlan,
  ]);

  const selectSinglePayment = useCallback(() => {
    dispatch({ type: actionTypes.SELECT_MONTHLY_PLAN, installmentsPlan: SINGLE_PAYMENT_PLAN });
    dispatch({ type: actionTypes.SET_SELECTED_PLAN, selectedPlan: 'singlePayment' });
  }, [dispatch]);

  const selectMonths = useCallback(
    (months: number) => {
      const plan = plans.find((candidate) => candidate.months === months);
      if (!plan) return;
      dispatch({ type: actionTypes.SELECT_MONTHLY_PLAN, installmentsPlan: plan });
      dispatch({ type: actionTypes.SET_SELECTED_PLAN, selectedPlan: 'monthly' });
    },
    [dispatch, plans],
  );

  return {
    cardPreview: card
      ? {
          brand: card.brand,
          cardType: card.kind === 'stored' ? card.cardType : undefined,
          expirationDate: card.kind === 'stored' ? card.expirationDate : undefined,
          last4: card.last4,
        }
      : undefined,
    eligibilityStatus: eligibility.status,
    plans,
    retryEligibility: eligibility.retry,
    selectMonths,
    selectSinglePayment,
    selectedMonths: selectedPlan?.months ?? 1,
    selectedPlan,
    showInterest,
    shouldRender:
      eligibility.status === 'checking' || eligibility.status === 'error' || canSelectInstallments,
    totalAmount,
  };
}

export type {
  InstallmentsCardDetails,
  InstallmentsCardPreview,
  InstallmentsEligibilityStatus,
  UseBasisTheoryInstallmentsResult,
};
export { useBasisTheoryInstallments, useInstallmentsEligibility };
